<template>
  <nav class="navbar" :class="{ 'scrolled': isScrolled, 'lang-en': locale === 'en-US' }">
    <div class="nav-container">
      <div class="nav-logo">
        <!-- LOGO 图片本身已包含图标 + 名称，不再叠加固定文字 -->
        <img class="logo-img" :src="companyLogo || '/logo/logo-zh.png'" alt="巨型智造" />
      </div>

      <div class="nav-menu">
        <router-link to="/" class="nav-item">
          <span class="nav-text">{{ $t('nav.home') }}</span>
          <span class="nav-indicator"></span>
        </router-link>

        <div class="nav-item nav-dropdown" @mouseenter="showDropdown('equipment')" @mouseleave="hideDropdown">
          <span class="nav-text">{{ $t('nav.equipment') }}</span>
          <span class="nav-indicator"></span>
          <div class="dropdown-menu" v-show="activeDropdown === 'equipment'">
            <router-link v-for="item in menu.equipment" :key="item.id" :to="detailPath('equipment', item.name)" class="dropdown-item">
              <div class="dropdown-title">{{ displayName(item) }}</div>
            </router-link>
          </div>
        </div>

        <div class="nav-item nav-dropdown" @mouseenter="showDropdown('services')" @mouseleave="hideDropdown">
          <span class="nav-text">{{ $t('nav.services') }}</span>
          <span class="nav-indicator"></span>
          <div class="dropdown-menu" v-show="activeDropdown === 'services'">
            <router-link v-for="item in menu.services" :key="item.id" :to="detailPath('services', item.name)" class="dropdown-item">
              <div class="dropdown-title">{{ displayName(item) }}</div>
            </router-link>
          </div>
        </div>

        <div class="nav-item nav-dropdown" @mouseenter="showDropdown('solutions')" @mouseleave="hideDropdown">
          <span class="nav-text">{{ $t('nav.solutions') }}</span>
          <span class="nav-indicator"></span>
          <div class="dropdown-menu" v-show="activeDropdown === 'solutions'">
            <router-link v-for="item in menu.solutions" :key="item.id" :to="detailPath('solutions', item.title || item.name)" class="dropdown-item">
              <div class="dropdown-title">{{ displayName(item) }}</div>
            </router-link>
          </div>
        </div>

        <router-link to="/news" class="nav-item">
          <span class="nav-text">{{ $t('nav.news') }}</span>
          <span class="nav-indicator"></span>
        </router-link>
        <router-link to="/about" class="nav-item">
          <span class="nav-text">{{ $t('nav.about') }}</span>
          <span class="nav-indicator"></span>
        </router-link>
      </div>

      <div class="nav-actions">
        <div class="lang-switcher">
          <span class="lang-option" :class="{ active: locale === 'zh-CN' }" @click="setLocale('zh-CN')">中文</span>
          <span class="lang-divider">|</span>
          <span class="lang-option" :class="{ active: locale === 'en-US' }" @click="setLocale('en-US')">EN</span>
        </div>
        <button class="btn-quote" @click="openQuote">
          <span>{{ $t('nav.quote') }}</span>
        </button>
      </div>

      <button class="nav-toggle" @click="mobileOpen = !mobileOpen" aria-label="菜单">
        <span class="toggle-bar" :class="{ 'bar-open': mobileOpen }"></span>
        <span class="toggle-bar" :class="{ 'bar-open': mobileOpen }"></span>
        <span class="toggle-bar" :class="{ 'bar-open': mobileOpen }"></span>
      </button>
    </div>

    <!-- 移动端菜单 -->
    <div class="mobile-menu" :class="{ 'menu-open': mobileOpen }">
      <router-link to="/" class="mobile-item" @click="mobileOpen = false">{{ $t('nav.home') }}</router-link>
      <div class="mobile-group">
        <div class="mobile-group-title">{{ $t('nav.equipment') }}</div>
        <router-link v-for="item in menu.equipment" :key="item.id" :to="detailPath('equipment', item.name)" class="mobile-sub" @click="mobileOpen = false">{{ displayName(item) }}</router-link>
      </div>
      <div class="mobile-group">
        <div class="mobile-group-title">{{ $t('nav.services') }}</div>
        <router-link v-for="item in menu.services" :key="item.id" :to="detailPath('services', item.name)" class="mobile-sub" @click="mobileOpen = false">{{ displayName(item) }}</router-link>
      </div>
      <div class="mobile-group">
        <div class="mobile-group-title">{{ $t('nav.solutions') }}</div>
        <router-link v-for="item in menu.solutions" :key="item.id" :to="detailPath('solutions', item.title || item.name)" class="mobile-sub" @click="mobileOpen = false">{{ displayName(item) }}</router-link>
      </div>
      <router-link to="/news" class="mobile-item" @click="mobileOpen = false">{{ $t('nav.news') }}</router-link>
      <router-link to="/about" class="mobile-item" @click="mobileOpen = false">{{ $t('nav.about') }}</router-link>
      <div class="mobile-lang">
        <span class="mobile-lang-option" :class="{ active: locale === 'zh-CN' }" @click="setLocale('zh-CN'); mobileOpen = false">中文</span>
        <span class="mobile-lang-divider">|</span>
        <span class="mobile-lang-option" :class="{ active: locale === 'en-US' }" @click="setLocale('en-US'); mobileOpen = false">EN</span>
      </div>
      <button class="mobile-item mobile-cta" @click="openQuote">{{ $t('nav.quote') }}</button>
    </div>
  </nav>
</template>

<script>
import { ref, reactive, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { equipmentListApi, serviceListApi, solutionListApi, companyApi } from '../api'
import { openInquiry } from '../utils/inquiry'
import { slugOf } from '../utils'

export default {
  name: 'Navigation',
  setup() {
    const { locale } = useI18n()
    const isScrolled = ref(false)
    const activeDropdown = ref(null)
    const mobileOpen = ref(false)
    let hideTimer = null

    // 导航下拉菜单数据：直接来自后台条目（后台有 3 条设备就显示 3 条，新增自动出现）
    const menu = reactive({ equipment: [], services: [], solutions: [] })

    const loadNavMenu = async () => {
      try {
        const [eq, svc, sol] = await Promise.all([
          equipmentListApi(),
          serviceListApi(),
          solutionListApi()
        ])
        menu.equipment = eq || []
        menu.services = svc || []
        menu.solutions = sol || []
      } catch (e) {
        // 拉取失败保持空菜单
      }
    }

    const displayName = (item) =>
      locale.value === 'en-US'
        ? (item.nameEn || item.titleEn || item.name || item.title)
        : (item.name || item.title)
    const detailPath = (type, name) => {
      const slug = slugOf(name)
      return slug ? `/${type}/${slug}` : `/${type}`
    }

    const setLocale = (l) => {
      locale.value = l
      localStorage.setItem('locale', l)
    }

    // 获取报价：打开全局咨询弹窗（关闭移动端菜单）
    const openQuote = () => {
      mobileOpen.value = false
      openInquiry()
    }

    const handleScroll = () => {
      isScrolled.value = window.scrollY > 20
    }

    const showDropdown = (menu) => {
      if (hideTimer) {
        clearTimeout(hideTimer)
        hideTimer = null
      }
      activeDropdown.value = menu
    }

    const hideDropdown = () => {
      hideTimer = setTimeout(() => {
        activeDropdown.value = null
      }, 200)
    }

    // 导航 LOGO：优先使用后台"公司信息"里配置的公司 logo，未配置时回退默认文件
    const companyLogo = ref('')
    const loadCompanyLogo = async () => {
      try {
        const c = await companyApi()
        companyLogo.value = (c && c.logo) || ''
      } catch (e) {
        // 拉取失败保持回退 logo
      }
    }

    onMounted(() => {
      window.addEventListener('scroll', handleScroll)
      loadNavMenu()
      loadCompanyLogo()
    })

    onUnmounted(() => {
      window.removeEventListener('scroll', handleScroll)
      if (hideTimer) {
        clearTimeout(hideTimer)
      }
    })

    return {
      isScrolled,
      activeDropdown,
      mobileOpen,
      locale,
      menu,
      companyLogo,
      displayName,
      detailPath,
      setLocale,
      openQuote,
      showDropdown,
      hideDropdown
    }
  }
}
</script>

<style scoped>
.navbar {
  background: #FFFFFF;
  height: 80px;
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  box-shadow: 0 1px 0 rgba(15, 23, 42, 0.06), 0 2px 10px rgba(15, 23, 42, 0.06);
  transition: all 0.3s ease;
}

.navbar.scrolled {
  height: 70px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.06), 0 6px 20px rgba(15, 23, 42, 0.08);
}

.nav-container {
  max-width: 1400px;
  height: 100%;
  margin: 0 auto;
  padding: 0 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.nav-logo {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-shrink: 0;
}

.logo-img {
  height: 46px;
  width: auto;
  max-width: 190px;
  object-fit: contain;
  display: block;
}

.logo-icon {
  display: flex;
  align-items: center;
  animation: float 3s ease-in-out infinite;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-5px);
  }
}

.nav-menu {
  display: flex;
  gap: 54px;
  align-items: center;
  flex-wrap: nowrap;
}

.nav-item {
  color: #1F2937;
  font-size: 18px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px 0 10px;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

.nav-text {
  transition: all 0.3s ease;
}

.nav-indicator {
  width: 0;
  height: 2px;
  background: #0B4F8C;
  transition: width 0.3s ease;
  border-radius: 2px;
}

.nav-item:hover .nav-text {
  color: #0B4F8C;
  transform: translateY(-2px);
}

.nav-item:hover .nav-indicator {
  width: 100%;
}

.nav-item.router-link-active .nav-text {
  color: #0B4F8C;
}

.nav-item.router-link-active .nav-indicator {
  width: 100%;
  background: #0B4F8C;
}

.nav-actions {
  display: flex;
  align-items: center;
  gap: 30px;
}

.lang-switcher {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #334155;
  font-size: 16px;
}

.lang-option {
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 4px 8px;
  border-radius: 4px;
}

.lang-option:hover {
  color: #0B4F8C;
  background: rgba(11, 79, 140, 0.08);
}

.lang-option.active {
  color: #0B4F8C;
  font-weight: 600;
  background: rgba(11, 79, 140, 0.12);
}

.lang-divider {
  color: rgba(15, 23, 42, 0.25);
}

.btn-quote {
  background: #0B4F8C;
  color: #FFFFFF;
  border: none;
  padding: 13px 34px;
  border-radius: 4px;
  font-size: 17px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
}

.btn-quote::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
  transition: left 0.5s ease;
}

.btn-quote:hover::before {
  left: 100%;
}

.btn-quote:hover {
  background: #1976D2;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(33, 150, 243, 0.4);
}

.btn-quote svg {
  transition: transform 0.3s ease;
}

.btn-quote:hover svg {
  transform: translateX(4px);
}
.nav-dropdown {
  position: relative;
  cursor: pointer;
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  background: #FFFFFF;
  border-radius: 2px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  padding: 8px;
  min-width: 280px;
  z-index: 1000;
  animation: dropdownSlide 0.3s ease;
}

.dropdown-menu::before {
  content: '';
  position: absolute;
  top: -16px;
  left: 0;
  right: 0;
  height: 16px;
}

@keyframes dropdownSlide {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  border-radius: 0;
  text-decoration: none;
  transition: all 0.3s ease;
  color: #1A1A1A;
  border-left: 3px solid transparent;
}

.dropdown-item:hover {
  background: #E3F2FD;
  border-left-color: #2196F3;
}

.dropdown-icon {
  font-size: 32px;
  flex-shrink: 0;
  display: none; /* 隐藏emoji图标 */
}

.dropdown-content {
  flex: 1;
}

.dropdown-title {
  font-size: 17px;
  font-weight: 600;
  color: #1A1A1A;
  margin-bottom: 0;
}

.dropdown-desc {
  font-size: 12px;
  color: #757575;
}

/* ===== 汉堡按钮（默认隐藏） ===== */
.nav-toggle {
  display: none;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  width: 44px;
  height: 44px;
  padding: 10px;
  background: transparent;
  border: none;
  cursor: pointer;
}

.toggle-bar {
  display: block;
  width: 24px;
  height: 2px;
  background: #0B4F8C;
  border-radius: 2px;
  transition: all 0.3s ease;
}

.toggle-bar.bar-open:nth-child(1) {
  transform: translateY(7px) rotate(45deg);
}

.toggle-bar.bar-open:nth-child(2) {
  opacity: 0;
}

.toggle-bar.bar-open:nth-child(3) {
  transform: translateY(-7px) rotate(-45deg);
}

/* ===== 移动端菜单 ===== */
.mobile-menu {
  display: none;
  position: fixed;
  top: 80px;
  left: 0;
  right: 0;
  max-height: calc(100vh - 80px);
  overflow-y: auto;
  background: #FFFFFF;
  padding: 16px 24px 32px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.12);
}

.mobile-item {
  display: block;
  color: #1F2937;
  font-size: 17px;
  font-weight: 600;
  padding: 14px 0;
  text-decoration: none;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
}

.mobile-group-title {
  color: #0B4F8C;
  font-size: 14px;
  font-weight: 700;
  padding: 16px 0 8px;
  letter-spacing: 1px;
}

.mobile-sub {
  display: block;
  color: #475569;
  font-size: 15px;
  padding: 10px 0 10px 16px;
  text-decoration: none;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
}

.mobile-cta {
  margin-top: 16px;
  text-align: center;
  background: #0B4F8C;
  color: #FFFFFF;
  border-radius: 40px;
  border: none;
  width: 100%;
  cursor: pointer;
  font-family: inherit;
}

.mobile-lang {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 16px 0 4px;
}

.mobile-lang-option {
  color: #334155;
  font-size: 15px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 20px;
  cursor: pointer;
  border: 1px solid rgba(15, 23, 42, 0.2);
}

.mobile-lang-option.active {
  color: #0B4F8C;
  background: rgba(11, 79, 140, 0.1);
  border-color: #0B4F8C;
}

.mobile-lang-divider {
  color: rgba(15, 23, 42, 0.3);
}

/* ===== 英文导航：英文单词更长，收紧菜单间距/字号，避免挤压换行 ===== */
.navbar.lang-en .nav-menu {
  gap: 32px;
}

.navbar.lang-en .nav-item {
  font-size: 16px;
  letter-spacing: 0;
}

.navbar.lang-en .nav-actions {
  gap: 20px;
}

.navbar.lang-en .lang-switcher {
  font-size: 15px;
  gap: 8px;
}

.navbar.lang-en .btn-quote {
  padding: 12px 24px;
  font-size: 16px;
}

/* ===== 移动端断点 ===== */
@media (max-width: 900px) {
  .nav-container {
    padding: 0 20px;
  }

  .nav-menu,
  .nav-actions {
    display: none;
  }

  .nav-toggle {
    display: flex;
  }

  .mobile-menu.menu-open {
    display: block;
  }

  .mobile-menu {
    padding-bottom: calc(32px + env(safe-area-inset-bottom, 0px));
  }
}

</style>
